import React from 'react';
import { useTranslation } from 'react-i18next';
import { isNativeApp } from '../../utils/platformUtils';
import RintintinLoaderNative from './RintintinLoaderNative';
import './RintintinLoader.css';

export interface RintintinLoaderProps {
  /** Taille du loader: 'small' (24px), 'medium' (48px), 'large' (64px), 'xlarge' (96px) */
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  /** Afficher en mode plein écran avec fond semi-transparent */
  fullscreen?: boolean;
  /** Texte à afficher sous le loader */
  text?: string;
  /** Variante de couleur: 'primary' (doré), 'white', 'dark' */
  variant?: 'primary' | 'white' | 'dark';
  /** Sans marge intérieure (ex. dans un bouton) */
  compact?: boolean;
  /** Classe CSS supplémentaire */
  className?: string;
}

/**
 * RintintinLoader — Spring Dots (3 points dorés, rebond ressort décalé).
 * Native → même animation (RintintinLoaderNative).
 */
const RintintinLoader: React.FC<RintintinLoaderProps> = (props) => {
  const { t } = useTranslation('common');
  if (isNativeApp()) {
    return <RintintinLoaderNative {...props} />;
  }

  const {
    size = 'large',
    fullscreen = false,
    text,
    variant = 'primary',
    compact = false,
    className = '',
  } = props;

  const sizeClass = `rintintin-loader--${size}`;
  const variantClass = `rintintin-loader--${variant}`;
  const inlineClass = compact
    ? 'rintintin-loader-inline rintintin-loader-inline--compact'
    : 'rintintin-loader-inline';
  const fullscreenClass = fullscreen ? 'rintintin-loader-fullscreen' : inlineClass;

  return (
    <div
      className={`rintintin-loader-wrapper ${fullscreenClass} ${className}`}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={text || t('loading')}
    >
      <div className={`rintintin-loader ${sizeClass} ${variantClass}`}>
        <span className="rintintin-loader__dot rintintin-loader__dot--0" />
        <span className="rintintin-loader__dot rintintin-loader__dot--1" />
        <span className="rintintin-loader__dot rintintin-loader__dot--2" />
      </div>

      {text ? (
        <p className={`rintintin-loader__text rintintin-loader__text--${variant}`}>{text}</p>
      ) : null}
    </div>
  );
};

export default RintintinLoader;
